import {
    DAuditInfo,
    IAuditStamps,
    RhCheckbox,
    RhDateInput,
    RhFormWrapper,
    RhNumberField,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf, ValidationError } from 'yup';
import {
    EXPENSE_FIELD_LABELS,
    FIELD_LABELS,
    getDateFromString,
    getStringFromDate,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import {
    ActivityEntityName,
    queryIsExpenseInternalCodeOccupied,
    useExpenseReceiptTypeIdInputQuery,
    useUpsertExpensesDialogMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import { useApolloClient } from '@apollo/client';
import { ExpenseAccountIdInput } from './account-id/purchase-account-id-input';
import { ExpenseReceiptTypeIdInput } from './receipt-type-id/receipt-type-id-input';
import { Box } from '@mui/material';
import { TaxInput } from './tax/tax';
import { TaxRetainedInput } from './tax-retained/tax-retained';
import { SupplementCodeInput } from './supplement-code/supplement-code';
import { ExpenseRequireSupplementInput } from './require-supplement/require-supplement';
import { NonTaxRetainedInput } from './non-tax-retained/non-tax-retained';
import { ExpenseResourcesColumns } from './expense-raw-material-additions-columns/expense-resources-columns';
import { ExpenseSupplierCatalogReference } from './expense-supplier-catalog-reference/expense-supplier-catalog-reference';
import { SubtotalInput } from './subtotal/subtotal';
import { TransferReceiptColumns } from './transfer-receipts/transfer-receipt-columns';
import { OrderCodeInput } from './order-code/order-code';
import { InvoiceCodeInput } from './invoice-code/invoice-code-input';
import { doesSubtotalMatchResourcesTotal } from './validation/does-subtotal-match-resources-total';
import { ExpenseDateInput } from './expense-date/expense-date-input';
import { TaxRequirementWarning } from '../../tax-requirement-warning';
import { ExpenseRequireTaxInput } from './require-tax/require-tax-input';
import { ExpenseIvaSplitter } from './tools/iva-splitter';
import { ExpenseRequireExternalCodeInput } from './require-external-code/require-external-code-input';
import {
    FinalAmountChangeWarning,
    useFinalAmountChangeConfirmation,
} from '../../use-final-amount-change-confirmation';
import { FormFieldSection } from '../../form-field-section';

export interface IExpense {
    id?: number | null;
    locked: boolean;
    require_supplement: boolean;
    canceled: boolean;
    reconciliation_only: boolean;
    is_draft: boolean;
    notes?: string;
    supplement_code?: string | null;
    date?: string | null;
    expected_payment_date?: string | null;
    account?: {
        id?: number | null;
        name: string;
        compound_name: string;
        resource_id?: number | null;
        monitor_supplier_expenses: boolean;
        // Supplier defaults used to pre-fill this expense when the account is
        // picked.
        supplier_credit_days?: number | null;
        supplier_require_supplement?: boolean | null;
        supplier_requires_external_code?: boolean | null;
        supplier_requires_tax?: boolean | null;
        supplier_reconciliation_only?: boolean | null;
        supplier_is_draft?: boolean | null;
    } | null;
    receipt_type_id?: number | null;
    require_external_code: boolean;
    require_tax: boolean;
    external_code?: string | null;
    internal_code?: number | null;
    tax: number;
    subtotal: number;
    tax_retained: number;
    non_tax_retained: number;
    expense_resources: {
        id?: number | null;
        units: number;
        unit_price?: number | null;
        resource_id?: number | null;
        date?: string | null;
        notes?: string;
    }[];
    resources_total?: number | null;
    total_with_tax: number;
    transfer_receipts?: {
        id?: number | null;
        amount: number;
        transferred_date?: string | null;
        transfer?: {
            transferred_date: string;
            from_account?: {
                compound_name: string;
            } | null;
        } | null;
        compound_name?: string | null;
    }[];
    transfer_receipts_total_no_adjustments?: number | null;
}

export const TAX: keyof IExpense = 'tax';
export const ACCOUNT: keyof IExpense = 'account';
export const TRANSFER_RECEIPTS: keyof IExpense = 'transfer_receipts';
export const TAX_RETAINED: keyof IExpense = 'tax_retained';
export const NON_TAX_RETAINED: keyof IExpense = 'non_tax_retained';
export const TOTAL_WITH_TAX: keyof IExpense = 'total_with_tax';
export const TRANSFER_RECEIPTS_TOTAL: keyof IExpense =
    'transfer_receipts_total_no_adjustments';
export const SUBTOTAL: keyof IExpense = 'subtotal';
export const REQUIRE_SUPPLEMENT: keyof IExpense = 'require_supplement';
export const REQUIRE_EXTERNAL_CODE: keyof IExpense = 'require_external_code';
export const REQUIRE_TAX: keyof IExpense = 'require_tax';
export const EXTERNAL_CODE: keyof IExpense = 'external_code';
export const CANCELED: keyof IExpense = 'canceled';
export const RECONCILIATION_ONLY: keyof IExpense = 'reconciliation_only';
export const IS_DRAFT: keyof IExpense = 'is_draft';
export const EXPENSE_RESOURCES: keyof IExpense = 'expense_resources';
export const RESOURCE_ID: keyof IExpense['expense_resources'][number] =
    'resource_id';
export const UNIT_PRICE: keyof IExpense['expense_resources'][number] =
    'unit_price';
export const UNITS: keyof IExpense['expense_resources'][number] = 'units';
export const RESOURCES_TOTAL: keyof IExpense = 'resources_total';

interface IExpensesForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of IExpense (see IAuditStamps).
    expense?: (IExpense & IAuditStamps) | null;
    onSuccess: () => void;
    disabled?: boolean;
}

export function ExpenseForm({
    expense,
    onSuccess,
    disabled = false,
}: IExpensesForm) {
    const [mutation, { loading }] = useUpsertExpensesDialogMutation();
    const apolloClient = useApolloClient();
    const { data: receiptTypesData } = useExpenseReceiptTypeIdInputQuery();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Expense,
        entityId: expense?.id,
        unsavedMessage:
            'El historial aparece una vez que la compra ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta compra.',
    });
    const { confirmFinalAmountChange, finalAmountChangeConfirmation } =
        useFinalAmountChangeConfirmation({
            initialAmount: expense?.total_with_tax ?? 0,
            isEditing: !!expense?.id,
            subject: 'El total del gasto',
        });

    const initialValues = useMemo<IExpense>(() => {
        return {
            id: expense?.id || undefined,
            notes: expense?.notes || '',
            date:
                expense && expense.date
                    ? getDateFromString(expense.date)
                    : null,
            tax: expense?.tax || 0,
            total_with_tax: expense?.total_with_tax || 0,
            subtotal: expense?.subtotal || 0,
            non_tax_retained: expense?.non_tax_retained || 0,
            tax_retained: expense?.tax_retained || 0,
            expected_payment_date:
                expense && expense.expected_payment_date
                    ? getDateFromString(expense.expected_payment_date)
                    : null,
            account:
                expense && expense.account
                    ? {
                          id: expense.account.id,
                          resource_id: expense.account.resource_id,
                          name: expense.account.name,
                          compound_name: expense.account.compound_name,
                          monitor_supplier_expenses:
                              expense.account.monitor_supplier_expenses,
                          supplier_requires_external_code:
                              expense.account.supplier_requires_external_code,
                          supplier_requires_tax:
                              expense.account.supplier_requires_tax,
                          supplier_is_draft:
                              expense.account.supplier_is_draft,
                      }
                    : null,
            receipt_type_id: expense ? expense.receipt_type_id : null,
            locked: expense?.locked || false,
            require_supplement: expense?.require_supplement || false,
            require_external_code: expense
                ? expense.require_external_code
                : false,
            require_tax: expense?.require_tax ?? false,
            canceled: expense?.canceled || false,
            reconciliation_only: expense?.reconciliation_only || false,
            // New expenses default to finalized; the supplier account prefill
            // overrides this to the account default once a proveedor is picked.
            is_draft: expense ? expense.is_draft : false,
            expense_resources:
                expense?.expense_resources && expense.expense_resources
                    ? expense?.expense_resources.map((er) => {
                          return {
                              id: er.id ?? null,
                              units: er.units,
                              resource_id: er.resource_id,
                              unit_price: er.unit_price,
                              date:
                                  er && er.date
                                      ? getDateFromString(er.date)
                                      : null,
                              notes: er.notes,
                          };
                      })
                    : [
                          {
                              id: null,
                              unit_price: 0,
                              units: 1,
                              resource_id: null,
                              date: null,
                              notes: '',
                          },
                      ],
            external_code: expense ? expense.external_code : '',
            internal_code: expense ? expense.internal_code : 0,
            supplement_code: expense ? expense.supplement_code : '',
            resources_total: expense ? expense.resources_total : 0,
            transfer_receipts: expense?.transfer_receipts?.map((tr) => {
                return {
                    id: tr.id,
                    transferred_date: tr.transfer?.transferred_date || null,
                    amount: tr.amount || 0,
                    to_account: null,
                    compound_name:
                        tr.transfer?.from_account?.compound_name || '',
                };
            }),
            transfer_receipts_total_no_adjustments:
                expense?.transfer_receipts_total_no_adjustments || 0,
        };
    }, [expense]);

    const validationSchema: SchemaOf<IExpense> = yup.object({
        id: yup.number().notRequired().nullable(),
        date: yup.string().nullable().required(),
        expected_payment_date: yup.string().nullable(),
        external_code: yup.string().nullable().notRequired(),
        internal_code: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .notRequired()
            .test('unique', async function (value, context): Promise<
                boolean | ValidationError
            > {
                if (!!value && value > 0) {
                    const result = await queryIsExpenseInternalCodeOccupied(
                        apolloClient,
                        {
                            InternalCode: value,
                            ExpenseId: expense?.id || null,
                        },
                    );
                    if (result.data.isExpenseInternalCodeOccupied) {
                        return context.createError({
                            message: `El folio de factura ${value} se encuentra en uso`,
                        });
                    }
                }
                return true;
            }),
        supplement_code: yup.string().nullable().notRequired(),
        require_supplement: yup.boolean().required(),
        require_external_code: yup.boolean().required(),
        require_tax: yup.boolean().required(),
        canceled: yup.boolean().required(),
        subtotal: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required('`Subtotal` es obligatorio')
            .test(
                'subtotal-matches-resources-total',
                doesSubtotalMatchResourcesTotal,
            ),
        total_with_tax: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required('`Total` es obligatorio'),
        tax: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required('`IVA` es obligatorio')
            .test(
                'tax-requires-taxable-receipt-type',
                'El tipo de gasto seleccionado no permite IVA; establece el IVA en 0',
                function (value) {
                    const receiptTypeId = this.parent.receipt_type_id;
                    const receiptType = (
                        receiptTypesData?.getReceiptTypes ?? []
                    ).find((type) => type.id === receiptTypeId);

                    return (
                        !receiptType ||
                        receiptType.applies_tax ||
                        Number(value) === 0
                    );
                },
            ),
        tax_retained: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required('`IVA retenido` es obligatorio')
            .test(
                'tax-retained-requires-taxable-receipt-type',
                'El tipo de gasto seleccionado no permite IVA retenido; establece el IVA retenido en 0',
                function (value) {
                    const receiptType = (
                        receiptTypesData?.getReceiptTypes ?? []
                    ).find((type) => type.id === this.parent.receipt_type_id);
                    return (
                        !receiptType ||
                        receiptType.applies_tax ||
                        Number(value) === 0
                    );
                },
            ),
        non_tax_retained: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required('`ISR retenido` es obligatorio')
            .test(
                'non-tax-retained-requires-taxable-receipt-type',
                'El tipo de gasto seleccionado no permite ISR retenido; establece el ISR retenido en 0',
                function (value) {
                    const receiptType = (
                        receiptTypesData?.getReceiptTypes ?? []
                    ).find((type) => type.id === this.parent.receipt_type_id);
                    return (
                        !receiptType ||
                        receiptType.applies_tax ||
                        Number(value) === 0
                    );
                },
            ),
        locked: yup.boolean().required(),
        reconciliation_only: yup.boolean().required(),
        is_draft: yup.boolean().required(),
        notes: yup.string(),
        account: yup.mixed().required(`'Proveedor' es obligatorio`).nullable(),
        receipt_type_id: yup
            .number()
            .transform(transformToNumber)
            .required(`'Tipo de recibo' es obligatorio`)
            .nullable(),
        expense_resources: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    units: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required('`Cantidad` es obligatorio'),
                    resource_id: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required('`Recurso` es obligatorio'),
                    unit_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    date: yup.string().nullable().notRequired(),
                    notes: yup.string(),
                }),
            )
            .required('Required'),
        resources_total: yup
            .number()
            .transform(transformToNumber)
            .required()
            .nullable(),
        transfer_receipts: yup.mixed().nullable(),
        transfer_receipts_total_no_adjustments: yup.mixed().notRequired(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                expense?.id ? (
                    <DAuditInfo
                        created_by={expense.created_by}
                        updated_by={expense.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                const confirmed = await confirmFinalAmountChange(
                    data.total_with_tax,
                );
                if (!confirmed) return;

                try {
                    await mutation({
                        variables: {
                            ExpenseUpsertInput: {
                                id: expense?.id || null,
                                date: getStringFromDate(data.date!),
                                expected_payment_date:
                                    data.expected_payment_date
                                        ? getStringFromDate(
                                              data.expected_payment_date!,
                                          )
                                        : null,
                                locked: data.locked || false,
                                require_supplement:
                                    data.require_supplement || false,
                                require_external_code:
                                    data.require_external_code || false,
                                require_tax: data.require_tax || false,
                                canceled: data.canceled || false,
                                reconciliation_only:
                                    data.reconciliation_only || false,
                                is_draft: data.is_draft || false,
                                notes: data.notes || '',
                                subtotal: data?.subtotal || 0,
                                supplement_code: data.supplement_code || '',
                                account_id: data.account?.id || null,
                                receipt_type_id: data.receipt_type_id || null,
                                external_code: data?.external_code || '',
                                internal_code: data?.internal_code || 0,
                                tax: data?.tax || 0,
                                tax_retained: data?.tax_retained || 0,
                                non_tax_retained: data?.non_tax_retained || 0,
                                resources_total: data?.resources_total || 0,
                                expense_resources:
                                    data?.expense_resources.map((erma) => {
                                        return {
                                            // Round-tripped so an unchanged
                                            // resource is updated in place
                                            // instead of deleted and recreated.
                                            id: erma.id ?? null,
                                            units: erma.units,
                                            resource_id: erma.resource_id,
                                            unit_price: erma.unit_price,
                                            notes: erma.notes ? erma.notes : '',
                                            date: erma.date
                                                ? getStringFromDate(erma.date!)
                                                : null,
                                        };
                                    }) || [],
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'Quién y cuándo',
                        fields: ['receipt_type_id', 'account', 'date'],
                        content: (
                            <>
                                <ExpenseReceiptTypeIdInput />
                                <ExpenseDateInput />
                                <ExpenseAccountIdInput />
                            </>
                        ),
                    },
                    {
                        // The product/resource lines live on their own tab,
                        // BEFORE Montos. Subtotal is derived from these lines
                        // (units × unit_price, see
                        // use-calculate-expense-resources-total) and shown
                        // read-only here; the grand total is shown read-only too
                        // so this tab reads as "what the resources add up to".
                        label: 'Recursos',
                        fields: [EXPENSE_RESOURCES, SUBTOTAL],
                        content: (
                            <>
                                <ExpenseResourcesColumns />
                                <ExpenseSupplierCatalogReference />
                                <SubtotalInput />
                                <RhNumberField
                                    disabled
                                    name={TOTAL_WITH_TAX}
                                    label={shortLabel(
                                        FIELD_LABELS.total_with_tax,
                                    )}
                                />
                            </>
                        ),
                    },
                    {
                        // Montos is now the tax/retentions tab: the subtotal
                        // (derived on Recursos) is echoed read-only, tax and
                        // retentions are captured here, and the grand total is
                        // shown read-only again so both tabs end on the total.
                        label: 'Montos',
                        fields: [
                            SUBTOTAL,
                            TAX,
                            REQUIRE_TAX,
                            TAX_RETAINED,
                            NON_TAX_RETAINED,
                            TOTAL_WITH_TAX,
                        ],
                        content: (
                            <>
                                <SubtotalInput />
                                <ExpenseRequireTaxInput />
                                <TaxInput />
                                <TaxRequirementWarning
                                    marginTop={0}
                                    marginBottom={2}
                                />
                                <TaxRetainedInput />
                                <NonTaxRetainedInput />
                                <RhNumberField
                                    disabled
                                    name={TOTAL_WITH_TAX}
                                    label={shortLabel(
                                        FIELD_LABELS.total_with_tax,
                                    )}
                                />
                                <FinalAmountChangeWarning
                                    amountFieldName={TOTAL_WITH_TAX}
                                    initialAmount={
                                        expense?.total_with_tax ?? 0
                                    }
                                    isEditing={!!expense?.id}
                                    subject={'El total del gasto'}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Folio y complemento',
                        optional: true,
                        fields: [REQUIRE_EXTERNAL_CODE, REQUIRE_SUPPLEMENT],
                        content: (
                            <>
                                <FormFieldSection title={'Folios'}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <ExpenseRequireExternalCodeInput />
                                    <Box sx={{ flex: 1 }}>
                                        <OrderCodeInput />
                                    </Box>
                                </Box>
                                <InvoiceCodeInput />
                                </FormFieldSection>
                                <FormFieldSection title={'Complemento de pago'}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <ExpenseRequireSupplementInput />
                                    <Box sx={{ flex: 1 }}>
                                        <SupplementCodeInput />
                                    </Box>
                                </Box>
                                </FormFieldSection>
                            </>
                        ),
                    },
                    {
                        label: 'Información adicional',
                        optional: true,
                        fields: [
                            'expected_payment_date',
                            CANCELED,
                            RECONCILIATION_ONLY,
                            IS_DRAFT,
                            'notes',
                        ],
                        content: (
                            <>
                                <FormFieldSection title={'Fechas'}>
                                <RhDateInput
                                    name={'expected_payment_date'}
                                    label={shortLabel(
                                        EXPENSE_FIELD_LABELS.expected_payment_date,
                                    )}
                                />
                                </FormFieldSection>
                                <FormFieldSection title={'Notas'}>
                                    <RHTextField
                                        name={'notes'}
                                        label={shortLabel(FIELD_LABELS.notes)}
                                        rows={3}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Control del gasto'}>
                                <RhCheckbox
                                    name={CANCELED}
                                    label={shortLabel(FIELD_LABELS.canceled)}
                                />
                                <RhCheckbox
                                    name={RECONCILIATION_ONLY}
                                    label={shortLabel(
                                        FIELD_LABELS.reconciliation_only,
                                    )}
                                />
                                <RhCheckbox
                                    name={IS_DRAFT}
                                    label={shortLabel(FIELD_LABELS.is_draft)}
                                />
                                </FormFieldSection>
                            </>
                        ),
                    },
                    {
                        label: 'Transferencias',
                        optional: true,
                        fields: [],
                        content: (
                            <>
                                <TransferReceiptColumns />
                                <RhNumberField
                                    disabled
                                    name={TRANSFER_RECEIPTS_TOTAL}
                                    label={shortLabel(
                                        FIELD_LABELS.transfer_receipts_total_no_adjustments,
                                    )}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Herramientas',
                        optional: true,
                        // Local calculator only — no form fields of its own; the
                        // button writes into `tax` and the single resource line.
                        fields: [],
                        content: <ExpenseIvaSplitter />,
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
            {/*<RhCheckbox name={'locked'} label={'Bloquear'} />*/}
            {finalAmountChangeConfirmation}
        </RhFormWrapper>
    );
}
